import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import permissionsApi from '../../services/permissions';
import DossierMobile from './DossierMobile';
import {
  JOURS_COURTS, PHASES, Plaque, dateCourte, depuisIso, duree, etatPieces, hhmm, idDossier, jourIso, phaseStatut, rangsStatuts,
} from './planningMobileUtils';
import './PlanningMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Planning atelier — version téléphone (2026-10-23, demande utilisateur).
//
// Public visé : la DIRECTION, qui consulte (où en est l'atelier, où en est
// telle voiture), et les CHEFS D'ATELIER, qui font avancer les statuts.
//
// Une liste, pas une grille :
//   - un atelier à la fois (même mémoire que la grille : `planning-dernier-atelier`),
//     sans choix « Tous » (décision utilisateur) ;
//   - triée par heure d'INTERVENTION dans cet atelier : une voiture reçue lundi
//     et travaillée mercredi apparaît mercredi. L'heure de réception reste en
//     petit sous l'heure (« RDV 07:30 ») ;
//   - un dossier dont les travaux passent par deux ateliers le même jour figure
//     dans les deux listes, chacune à son heure (décision utilisateur) — c'est
//     ce que fait déjà la grille, une carte par placement ;
//   - 5 puces de phase qui regroupent les statuts atelier (validées le 23/10).
//
// Mêmes routes et mêmes capacités (`/planning/mon-role`) que la grille : rien
// de nouveau côté serveur.
// ═══════════════════════════════════════════════════════════════════════════

const CLE_ATELIER = 'planning-dernier-atelier';

const decaler = (iso, n) => { const d = depuisIso(iso); d.setDate(d.getDate() + n); return jourIso(d); };
const lundiDe = (iso) => { const d = depuisIso(iso); const dow = d.getDay() || 7; d.setDate(d.getDate() - dow + 1); return jourIso(d); };
const isoDow = (iso) => depuisIso(iso).getDay() || 7;

const phaseDe = (intv, arrivee, refs) => {
  if (intv.restituee_at) return 'restitue';
  const p = phaseStatut(intv.statut_atelier_id, intv.statut_atelier_nom, refs);
  if (p === 'restitue' || p === 'prets') return p;
  if (arrivee && (arrivee.statut === 'en_attente' || arrivee.statut === 'en_cours')) return 'surplace';
  return p;
};

function Carte({ intv, arrivee, avecDate, onOuvrir }) {
  const pieces = etatPieces(intv);
  const travaux = intv.description || intv.type_intervention || 'Travaux non précisés';
  return (
    <button type="button" className="pm-carte" style={{ '--pm-statut': intv.statut_atelier_couleur || 'var(--border-strong)' }} onClick={() => onOuvrir(intv)}>
      <span className="pm-carte-ident">
        <Plaque immat={intv.immat || intv.immatriculation} />
        <span className="pm-carte-client">{intv.client || [intv.client_prenom, intv.client_nom].filter(Boolean).join(' ') || 'Client non renseigné'}</span>
      </span>
      <span className="pm-carte-heure">
        {avecDate && <span className="pm-carte-date">{dateCourte(intv.date)}</span>}
        {intv.heure_debut || '—'}
        {intv.heure_reception && <span className="pm-carte-rdv">RDV {intv.heure_reception}</span>}
      </span>
      <span className="pm-carte-travaux">{travaux} · {duree(intv.duree)}</span>
      <span className="pm-carte-meta">
        <span className="pm-statut">{intv.statut_atelier_nom || 'Aucun statut'}</span>
        {arrivee?.statut === 'en_attente' && <span className="pm-pill pm-pill--accent">Arrivé {hhmm(arrivee.heure_arrivee)}</span>}
        {arrivee?.statut === 'en_cours' && <span className="pm-pill pm-pill--accent">En réception</span>}
        {pieces === 'disponible' && <span className="pm-pill pm-pill--ok">Pièces dispo</span>}
        {pieces === 'en_commande' && <span className="pm-pill pm-pill--attente">Pièces en attente</span>}
        {pieces === 'indisponible' && <span className="pm-pill pm-pill--ko">Pièce indispo</span>}
        {intv.client_attend && <span className="pm-pill">Client attend</span>}
        {intv.a_courtoisie && <span className="pm-pill">Courtoisie</span>}
        {intv.is_complement && <span className="pm-pill">Suite</span>}
        {intv.numero_or ? <span className="pm-pill">OR {intv.numero_or}</span> : <span className="pm-pill pm-pill--discret">OR à préparer</span>}
      </span>
    </button>
  );
}

function PlanningMobile({ setHeaderAction, onVersionOrdinateur }) {
  const aujourdhui = jourIso(new Date());
  const [jour, setJour] = useState(aujourdhui);
  const [ateliers, setAteliers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [atelierId, setAtelierId] = useState(() => { try { return localStorage.getItem(CLE_ATELIER) || ''; } catch (e) { return ''; } });
  const [interventions, setInterventions] = useState([]);
  const [fileAttente, setFileAttente] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [phase, setPhase] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState(null); // null = pas de recherche en cours
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [tourRecherche, setTourRecherche] = useState(0); // relance après une modification
  const [ouvert, setOuvert] = useState(null);       // ligne de liste du dossier ouvert
  const [planningRole, setPlanningRole] = useState(null);
  const [droits, setDroits] = useState(null);

  // La page n'a pas d'action d'en-tête sur téléphone : la grille en pose une
  // (Atelier / Réception), elle ne doit pas rester affichée ici.
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);

  useEffect(() => {
    api.get('/planning/mon-role').then(r => setPlanningRole(r.data || { role: null })).catch(() => setPlanningRole({ role: null }));
    permissionsApi.checkAccess('planning').then(r => setDroits(r.data?.permissions || r.data || null)).catch(() => setDroits(null));
  }, []);
  const capacites = useMemo(() => new Set(planningRole?.capacites || []), [planningRole]);
  const peut = useCallback((c) => capacites.has(c), [capacites]);
  const lectureSeuleDroits = !!droits && droits.peut_voir && !droits.peut_creer && !droits.peut_modifier && !droits.peut_supprimer;

  // Référentiels : ils ne bougent pas en cours de journée.
  useEffect(() => {
    Promise.allSettled([
      api.get('/planning/ateliers'),
      api.get('/planning/staff'),
      api.get('/referentiels/statuts-atelier'),
    ]).then(([a, s, st]) => {
      if (a.status === 'fulfilled') setAteliers((a.value.data || []).filter(x => x.actif !== false));
      if (s.status === 'fulfilled') setStaff(s.value.data || []);
      if (st.status === 'fulfilled') {
        const liste = (st.value.data?.statuts_atelier || []).filter(x => x.actif !== false);
        liste.sort((x, y) => (x.ordre ?? 0) - (y.ordre ?? 0) || x.id - y.id);
        setStatuts(liste);
      }
    });
  }, []);

  // Atelier mémorisé inconnu (autre concession, atelier fermé) : premier de la liste.
  useEffect(() => {
    if (!ateliers.length) return;
    if (!ateliers.some(a => String(a.id) === String(atelierId))) setAtelierId(String(ateliers[0].id));
  }, [ateliers, atelierId]);
  useEffect(() => {
    if (atelierId) { try { localStorage.setItem(CLE_ATELIER, String(atelierId)); } catch (e) { /* stockage indisponible */ } }
  }, [atelierId]);

  const lundi = lundiDe(jour);
  const semaine = useMemo(() => Array.from({ length: 7 }, (_, k) => decaler(lundi, k)), [lundi]);

  // Une semaine entière d'un coup : le bandeau de dates affiche le nombre de
  // dossiers de chaque jour, et passer d'un jour à l'autre reste instantané.
  const charger = useCallback(async () => {
    try {
      const { data } = await api.get('/planning/interventions', { params: { date_from: lundi, date_to: decaler(lundi, 6) } });
      setInterventions(data.interventions || []);
      setErreur('');
    } catch (e) {
      console.error('[planning-mobile]', e);
      setErreur('Le planning ne s’est pas chargé. Vérifiez la connexion puis rouvrez la page.');
    } finally {
      setChargement(false);
    }
  }, [lundi]);
  useEffect(() => { setChargement(true); charger(); }, [charger]);

  // File d'accueil : seulement le jour même, comme la grille.
  const chargerFile = useCallback(() => {
    if (jour !== aujourdhui || !peut('voir.onglet_reception')) { setFileAttente([]); return; }
    api.get('/planning/file-attente', { params: { date: jour } })
      .then(r => setFileAttente(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFileAttente([]));
  }, [jour, aujourdhui, peut]);
  useEffect(() => {
    chargerFile();
    const t = setInterval(chargerFile, 30000);
    return () => clearInterval(t);
  }, [chargerFile]);

  // Mise à jour en direct : même flux que la grille.
  const rechargerRef = useRef(() => {});
  useEffect(() => { rechargerRef.current = () => { charger(); chargerFile(); }; }, [charger, chargerFile]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || typeof EventSource === 'undefined') return undefined;
    const source = new EventSource(`/api/planning/events?token=${token}`);
    let minuterie = null;
    source.addEventListener('planning-updated', () => {
      clearTimeout(minuterie);
      minuterie = setTimeout(() => rechargerRef.current(), 300);
    });
    return () => { clearTimeout(minuterie); source.close(); };
  }, []);

  const refs = useMemo(() => rangsStatuts(statuts), [statuts]);
  const atelier = ateliers.find(a => String(a.id) === String(atelierId)) || null;
  const plusieursConcessions = new Set(ateliers.map(a => a.concession_id)).size > 1;
  const membres = useMemo(
    () => new Set(staff.filter(s => String(s.atelier_id) === String(atelierId)).map(s => s.id)),
    [staff, atelierId]
  );
  const arriveeDe = useCallback((i) => (i.date === aujourdhui ? fileAttente.find(f => f.intervention_id === idDossier(i)) : null), [fileAttente, aujourdhui]);

  // Un placement (dossier ou suite) par ligne. Un dossier placé deux fois dans
  // le même atelier le même jour n'apparaît qu'une fois, à sa première heure.
  const lignesDuJour = useCallback((iso) => {
    const vus = new Map();
    for (const i of interventions) {
      if (i.date !== iso || !membres.has(i.mecanicien_id)) continue;
      const id = idDossier(i);
      const deja = vus.get(id);
      if (!deja || String(i.heure_debut || '') < String(deja.heure_debut || '')) vus.set(id, i);
    }
    return [...vus.values()].sort((a, b) => String(a.heure_debut || '').localeCompare(String(b.heure_debut || '')));
  }, [interventions, membres]);

  const lignes = useMemo(() => lignesDuJour(jour), [lignesDuJour, jour]);
  const comptes = useMemo(() => {
    const c = { tous: lignes.length };
    lignes.forEach(i => { const p = phaseDe(i, arriveeDe(i), refs); c[p] = (c[p] || 0) + 1; });
    return c;
  }, [lignes, arriveeDe, refs]);
  const visibles = useMemo(
    () => (phase === 'tous' ? lignes : lignes.filter(i => phaseDe(i, arriveeDe(i), refs) === phase)),
    [lignes, phase, arriveeDe, refs]
  );

  const joursOuverts = atelier?.jours_ouverture?.length ? atelier.jours_ouverture : [1, 2, 3, 4, 5, 6];

  // Recherche : même route que la loupe de la grille, toutes dates confondues.
  useEffect(() => {
    const q = recherche.trim();
    if (q.length < 2) { setResultats(null); setRechercheEnCours(false); return undefined; }
    setRechercheEnCours(true);
    const t = setTimeout(() => {
      api.get('/planning/interventions', { params: { search: q, inclure_a_planifier: '1' } })
        .then(r => setResultats(r.data.interventions || []))
        .catch(() => setResultats([]))
        .finally(() => setRechercheEnCours(false));
    }, 300);
    return () => clearTimeout(t);
  }, [recherche, tourRecherche]);

  const groupesRecherche = useMemo(() => {
    if (!resultats) return [];
    const auj = [], avenir = [], passes = [];
    resultats.forEach(i => { (i.date === aujourdhui ? auj : i.date > aujourdhui ? avenir : passes).push(i); });
    avenir.sort((a, b) => `${a.date}${a.heure_debut}`.localeCompare(`${b.date}${b.heure_debut}`));
    return [['Aujourd’hui', auj], ['À venir', avenir], ['Passés', passes]].filter(([, l]) => l.length);
  }, [resultats, aujourdhui]);

  const listeOuverte = resultats ? groupesRecherche.flatMap(([, l]) => l) : visibles;
  const ouvrir = (intv) => { if (peut('dossier.ouvrir')) setOuvert(intv); };

  return (
    <div className="pm-page">
      <div className="pm-outils">
        <label className="pm-recherche" htmlFor="pm-recherche">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>
          <input
            id="pm-recherche"
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Immat, nom, n° OR, téléphone"
            autoComplete="off"
            enterKeyHint="search"
          />
          {recherche && <button type="button" className="pm-recherche-effacer" onClick={() => setRecherche('')} aria-label="Effacer la recherche">✕</button>}
        </label>
        {!resultats && (
          <>
            <div className="pm-ligne-atelier">
              <select
                className="pm-atelier"
                value={atelierId}
                onChange={(e) => { setAtelierId(e.target.value); setPhase('tous'); }}
                aria-label="Atelier"
              >
                {ateliers.map(a => (
                  <option key={a.id} value={a.id}>{plusieursConcessions ? `${a.nom} · ${a.concession_nom}` : a.nom}</option>
                ))}
              </select>
              <button type="button" className="pm-semaine-btn" onClick={() => setJour(decaler(jour, -7))} aria-label="Semaine précédente">‹</button>
              <button type="button" className="pm-semaine-btn" onClick={() => setJour(aujourdhui)} disabled={jour === aujourdhui}>Auj.</button>
              <button type="button" className="pm-semaine-btn" onClick={() => setJour(decaler(jour, 7))} aria-label="Semaine suivante">›</button>
            </div>
            <div className="pm-jours">
              {semaine.map(iso => {
                const ferme = !joursOuverts.includes(isoDow(iso));
                if (ferme && isoDow(iso) === 7) return null;
                const d = depuisIso(iso);
                return (
                  <button
                    type="button"
                    key={iso}
                    className={`pm-jour${iso === jour ? ' is-actif' : ''}${ferme ? ' is-ferme' : ''}${iso === aujourdhui ? ' is-auj' : ''}`}
                    onClick={() => setJour(iso)}
                    disabled={ferme}
                  >
                    <small>{JOURS_COURTS[d.getDay()]}</small>
                    <b>{d.getDate()}</b>
                    <i>{ferme ? 'fermé' : (chargement ? '·' : lignesDuJour(iso).length)}</i>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {!resultats && (
        <div className="pm-phases">
          {PHASES.map(p => (
            <button type="button" key={p.cle} className={`pm-phase${phase === p.cle ? ' is-actif' : ''}`} onClick={() => setPhase(p.cle)}>
              {p.libelle}<span>{comptes[p.cle] || 0}</span>
            </button>
          ))}
        </div>
      )}

      <div className="pm-liste">
        {erreur && <div className="pm-vide pm-vide--erreur">{erreur}</div>}
        {resultats ? (
          <>
            {groupesRecherche.map(([titre, liste]) => (
              <React.Fragment key={titre}>
                <div className="pm-groupe"><span>{titre}</span><span>{liste.length}</span></div>
                {liste.map(i => <Carte key={i.id} intv={i} avecDate={titre !== 'Aujourd’hui'} arrivee={arriveeDe(i)} onOuvrir={ouvrir} />)}
              </React.Fragment>
            ))}
            {!rechercheEnCours && resultats.length === 0 && (
              <div className="pm-vide">Aucun dossier pour « {recherche.trim()} ».<br />La recherche porte sur toutes les dates : immatriculation, nom, n° d’OR, téléphone.</div>
            )}
            {resultats.length >= 200 && <div className="pm-vide">Les 200 dossiers les plus récents sont affichés. Précisez la recherche pour trouver un dossier plus ancien.</div>}
          </>
        ) : (
          <>
            {!chargement && !erreur && (
              <div className="pm-groupe"><span>{dateCourte(jour)}{jour === aujourdhui ? ' · aujourd’hui' : ''}</span><span>{visibles.length} dossier{visibles.length > 1 ? 's' : ''}</span></div>
            )}
            {visibles.map(i => <Carte key={i.id} intv={i} arrivee={arriveeDe(i)} onOuvrir={ouvrir} />)}
            {!chargement && !erreur && visibles.length === 0 && (
              <div className="pm-vide">{phase === 'tous' ? 'Aucun dossier planifié dans cet atelier ce jour-là.' : 'Aucun dossier dans cette phase ce jour-là.'}</div>
            )}
            {chargement && <div className="pm-vide">Chargement du planning…</div>}
          </>
        )}
        <button type="button" className="pm-lien-ordinateur" onClick={() => onVersionOrdinateur && onVersionOrdinateur()}>
          Version ordinateur (grille)
        </button>
      </div>

      {ouvert && (
        <DossierMobile
          ligne={ouvert}
          liste={listeOuverte}
          onNaviguer={setOuvert}
          statuts={statuts}
          refs={refs}
          peut={peut}
          lectureSeuleDroits={lectureSeuleDroits}
          onFerme={() => setOuvert(null)}
          onModifie={() => { charger(); if (resultats) setTourRecherche(n => n + 1); }}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
    </div>
  );
}

export default PlanningMobile;
